import api from '../../config/api';
import { CollectionResponse } from '../../interfaces/api/api';
import { getJsonResponse } from '../../utils/ApiUtils';
import Contact, { CreateContactOptions, EditContactOptions } from '../../interfaces/api/Contact';
import useAuth from './useAuth';

const useContact = () => {
  const { fetch } = useAuth();

  const create = async (siteId: string, contact: CreateContactOptions): Promise<Contact> => {
    const { endpoint, ...options } = api.contact.create(siteId, contact);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Contact>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const update = async (contactId: string, contact: EditContactOptions): Promise<Contact> => {
    const { endpoint, ...options } = api.contact.update(contactId, contact);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Contact>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const getByCustomerAccess = async (customerAccessIds: string[]): Promise<CollectionResponse<Contact>> => {
    const { endpoint, ...options } = api.contact.getByCustomerAccess(customerAccessIds);
    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<CollectionResponse<Contact>>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const getBySite = async (siteId: string): Promise<Contact[]> => {
    const { endpoint, ...options } = api.contact.getBySite(siteId);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Contact[]>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const getByEmail = async (email: string): Promise<Contact> => {
    const { endpoint, ...options } = api.contact.getByEmail(email);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Contact>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const getByLegacyId = async (legacyId: number): Promise<Contact> => {
    const { endpoint, ...options } = api.contact.getByLegacyId(legacyId);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Contact>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const getAll = async (name: string, page: number, limit: number): Promise<CollectionResponse<Contact>> => {
    const { endpoint, ...options } = api.contact.getAll(name, page, limit);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<CollectionResponse<Contact>>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const get = async (id: string): Promise<Contact> => {
    const { endpoint, ...options } = api.contact.get(id);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Contact>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const disable = async (id: string): Promise<Contact> => {
    const { endpoint, ...options } = api.contact.disable(id);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Contact>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const enable = async (id: string): Promise<Contact> => {
    const { endpoint, ...options } = api.contact.enable(id);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Contact>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  return { create, update, getBySite, getByEmail, getByLegacyId, getAll, getByCustomerAccess, disable, enable, get };
};

export default useContact;
